import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { AppLink, Button, Card, ErrorText, Flex, Heading, Page, PageHeader, Table, Text } from '@cmaas/ui';
import type { AdminPortalUser, AdminProperty, Tenant } from '../../api/types';
import { apiFetch, CmaasApiError } from '../../api/client';
import { usePage } from '../../api/usePage';
import { EditTenantModal } from './EditTenantModal';
import { NewPortalUserModal } from './NewPortalUserModal';
import { NewTenantPropertyModal } from './NewTenantPropertyModal';

const mutationError = (err: unknown) => (err instanceof CmaasApiError ? err.message : 'Something went wrong.');

export function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const queryClient = useQueryClient();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [propertyModalOpen, setPropertyModalOpen] = useState(false);
  const [portalUserModalOpen, setPortalUserModalOpen] = useState(false);

  const tenantQuery = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => apiFetch<Tenant>(`/v1/admin/tenants/${tenantId}`),
  });

  const toggleTenantStatus = useMutation({
    mutationFn: ({ status }: { status: 'active' | 'disabled' }) =>
      apiFetch(`/v1/admin/tenants/${tenantId}/actions/${status === 'active' ? 'disable' : 'enable'}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  const propertiesKey = ['tenant-properties', tenantId];
  const propertiesPage = usePage<AdminProperty>(propertiesKey, '/v1/admin/properties', { tenant_id: tenantId });

  const portalUsersKey = ['tenant-portal-users', tenantId];
  const portalUsersPage = usePage<AdminPortalUser>(portalUsersKey, `/v1/admin/tenants/${tenantId}/portal-users`);

  const togglePortalUserStatus = useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: 'active' | 'disabled' }) =>
      apiFetch<AdminPortalUser>(`/v1/admin/tenants/${tenantId}/portal-users/${userId}`, {
        method: 'PATCH',
        body: { status: status === 'active' ? 'disabled' : 'active' },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: portalUsersKey }),
  });

  if (tenantQuery.isLoading) return <Text as="p">Loading…</Text>;
  if (!tenantQuery.data) return <Text as="p">Tenant not found.</Text>;
  const tenant = tenantQuery.data;
  const properties = propertiesPage.data?.pages.flatMap((p) => p.data) ?? [];
  const portalUsers = portalUsersPage.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <Page>
      <PageHeader
        title={tenant.name}
        actions={
          <Flex gap="2">
            <Button type="button" variant="soft" size="1" onClick={() => setEditModalOpen(true)}>
              Edit tenant
            </Button>
            <Button
              type="button"
              variant="soft"
              size="1"
              onClick={() => toggleTenantStatus.mutate({ status: tenant.status })}
              disabled={toggleTenantStatus.isPending}
            >
              {tenant.status === 'active' ? 'Disable tenant' : 'Enable tenant'}
            </Button>
          </Flex>
        }
      />
      <EditTenantModal tenant={tenant} open={editModalOpen} onOpenChange={setEditModalOpen} />
      <Card>
        <Flex direction="column" gap="2">
          <Text as="p">
            <Text weight="bold">Status:</Text> {tenant.status}
          </Text>
          <Text as="p">
            <Text weight="bold">Tenant id:</Text> {tenant.id}
          </Text>
          <Text as="p">
            <Text weight="bold">Created:</Text> {new Date(tenant.created_at).toLocaleString()}
          </Text>
        </Flex>
      </Card>
      {toggleTenantStatus.isError && <ErrorText>{mutationError(toggleTenantStatus.error)}</ErrorText>}

      <section>
        <Flex justify="between" align="center">
          <Heading size="5">Properties</Heading>
          <Button onClick={() => setPropertyModalOpen(true)}>+ Add property</Button>
        </Flex>

        <NewTenantPropertyModal tenantId={tenantId!} open={propertyModalOpen} onOpenChange={setPropertyModalOpen} />

        {propertiesPage.isLoading ? (
          <Text as="p">Loading…</Text>
        ) : (
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Platform</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Timezone</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {properties.map((p) => (
                <Table.Row key={p.id}>
                  <Table.Cell>
                    <AppLink to={`/properties/${p.id}`}>{p.name}</AppLink>
                  </Table.Cell>
                  <Table.Cell>{p.apple_platform}</Table.Cell>
                  <Table.Cell>{p.timezone}</Table.Cell>
                  <Table.Cell>{p.status}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
        {propertiesPage.hasNextPage && (
          <Button
            type="button"
            variant="soft"
            onClick={() => void propertiesPage.fetchNextPage()}
            disabled={propertiesPage.isFetchingNextPage}
          >
            Load more
          </Button>
        )}
      </section>

      <section>
        <Flex justify="between" align="center">
          <Heading size="5">Portal users</Heading>
          <Button onClick={() => setPortalUserModalOpen(true)}>+ Add portal user</Button>
        </Flex>

        <NewPortalUserModal tenantId={tenantId!} open={portalUserModalOpen} onOpenChange={setPortalUserModalOpen} />

        {portalUsersPage.isLoading ? (
          <Text as="p">Loading…</Text>
        ) : portalUsers.length === 0 ? (
          <Text as="p">No portal users yet.</Text>
        ) : (
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Role</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {portalUsers.map((u) => (
                <Table.Row key={u.id}>
                  <Table.Cell>{u.name}</Table.Cell>
                  <Table.Cell>{u.email}</Table.Cell>
                  <Table.Cell>{u.role}</Table.Cell>
                  <Table.Cell>{u.status}</Table.Cell>
                  <Table.Cell>
                    <Button
                      type="button"
                      variant="soft"
                      size="1"
                      onClick={() => togglePortalUserStatus.mutate({ userId: u.id, status: u.status })}
                      disabled={togglePortalUserStatus.isPending}
                    >
                      {u.status === 'active' ? 'Disable' : 'Enable'}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
        {portalUsersPage.hasNextPage && (
          <Button
            type="button"
            variant="soft"
            onClick={() => void portalUsersPage.fetchNextPage()}
            disabled={portalUsersPage.isFetchingNextPage}
          >
            Load more
          </Button>
        )}
        {togglePortalUserStatus.isError && <ErrorText>{mutationError(togglePortalUserStatus.error)}</ErrorText>}
      </section>
    </Page>
  );
}
